<template>
  <div class="bg-muted-100 dark:bg-muted-900 relative w-full overflow-hidden px-4">
    <BaseProgress
      v-if="page.isBusy"
      title="indeterminate primary progress bar"
      size="xs"
      class="fixed left-0 top-0 z-50 h-2 w-full"
    />
    <div class="flex w-full items-center justify-center">
      <div class="relative mx-auto flex w-full max-w-2xl flex-col items-center">
        <SkyVisitorLogo />
        <BaseParagraph size="sm" class="text-muted-400 my-4">
          {{ $t('sso.body') }}
        </BaseParagraph>
        <div class="me-auto ms-auto w-full">
          <div class="mx-auto me-auto ms-auto w-full max-w-lg px-8">
            <div class="flex flex-wrap justify-between gap-4">
              <a
                href="login"
                class="dark:bg-muted-700 text-muted-800 border-muted-300 dark:border-muted-600 nui-focus relative inline-flex grow items-center justify-center gap-2 rounded border bg-white px-6 py-4 dark:text-white"
              >
                <Icon name="ph:key" class="size-5" />
                <div>Single Sign On</div>
              </a>
            </div>

            <div class="flex-100 my-8 flex items-center">
              <hr class="border-muted-200 dark:border-muted-700 flex-auto border-t-2">
              <span
                class="bg-muted-100 dark:bg-muted-900 text-muted-400 relative top-0.5 px-4 text-center font-sans text-sm"
              >
                {{ $t("login.continue_with") }}
              </span>
              <hr class="border-muted-200 dark:border-muted-700 flex-auto border-t-2">
            </div>
          </div>
          <Form
            ref="formRef"
            v-slot="{ values }"
            method="POST"
            action=""
            class="mx-auto me-auto ms-auto w-full max-w-lg px-4"
            @submit="onSubmit"
          >
            <div class="px-8 pb-4">
              <div>
                <Field
                  v-slot="{ field, errorMessage, handleChange, handleBlur }"
                  name="username"
                  type="email"
                  :rules="validateUserNameRequired"
                >
                  <BaseInput
                    :model-value="field.value"
                    :error="errorMessage"
                    :disabled="page.isBusy"
                    type="email"
                    :label="$t('login.username')"
                    :placeholder="$t('login.username')"
                    :classes="{
                      input: 'h-12',
                    }"
                    @update:model-value="handleChange"
                    @blur="handleBlur"
                  />
                </Field>

                <Field
                  v-slot="{ field, errorMessage, handleChange, handleBlur }"
                  name="password"
                  :rules="validateRequired"
                >
                  <BaseInput
                    :model-value="field.value"
                    :error="errorMessage"
                    :disabled="page.isBusy"
                    type="password"
                    :label="$t('login.password')"
                    :placeholder="$t('login.password')"
                    :classes="{
                      input: 'h-12',
                    }"
                    @update:model-value="handleChange"
                    @blur="handleBlur"
                  />
                </Field>
              </div>
              <div class="mb-6">
                <div class="flex items-center justify-end">
                  <Field name="resetPassword">
                    <NuxtLink
                      to="/password"
                      class="text-primary-400 hover:text-primary-300 mt-3 font-sans text-sm underline-offset-4 transition duration-150 ease-in-out hover:underline"
                    >
                      {{ $t("login.forgot_password") }}
                    </NuxtLink>
                  </Field>
                </div>
              </div>
              <div class="mb-6">
                <BaseButton
                  :disabled="page.isBusy"
                  :loading="page.isBusy"
                  type="submit"
                  color="primary"
                  class="!h-12 w-full"
                >
                  {{ $t("login.button_log_in") }}
                </BaseButton>
              </div>
              <div class="mb-6 flex justify-center">
                <a
                  :href="config.public.webUrl+'/Ext/Login.aspx'"
                  class="text-primary-400 hover:text-primary-300 inline-flex items-center gap-2 font-sans text-sm underline-offset-4 transition duration-150 ease-in-out hover:underline"
                >
                  <Icon name="ph:hard-hat" class="size-4" />
                  <span>Contractor Login</span>
                </a>
              </div>
              <!--No account link-->
              <p class="text-muted-400 mt-4 flex justify-between font-sans text-sm leading-5">
                {{ $t("upgrade.upgrade_sso") }}
                <NuxtLink
                  to="https://share.hsforms.com/16KBUQsMvT8-tegKMIrEXBA5jl2a"
                  class="text-primary-400 hover:text-primary-300 font-sans text-sm underline-offset-4 transition duration-150 ease-in-out hover:underline"
                  target="_blank"
                >
                  <span>{{ $t("upgrade.click_here") }}</span>
                </NuxtLink>
              </p>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { Field, Form } from 'vee-validate'
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
useHead({
  title: 'Login',
})

definePageMeta({
  breadcrumb: {
    label: 'Admin',
  },
})

const config = useRuntimeConfig()
const { locale, t } = useI18n()

// Get the store instance
const {
  clear: clearAuth,
  user: authStoreUser,
  clear,
  setUser,
  setUserTenants,
} = useAuthStore()

const router = useRouter()
const { $dataService, $notification } = useNuxtApp()

// Watch the value of the dropdown and set i18n accordingly
watch(locale, () => {
  requireMessage.value = t('validation.required')
  resetForm() // HACK: Hide all error messages
})

definePageMeta({
  title: 'Login',
  preview: {
    title: 'SkyVisitor Login',
    description: 'For authentication and sign in',
    categories: ['authentication'],
    src: '/img/screens/auth-login-3.png',
    srcDark: '/img/screens/auth-login-3-dark.png',
    order: 98,
  },
})

const finalMicrosoftOAuthUrl = computed(() => {
  const config = useRuntimeConfig()
  const baseURL = `${config.public.apiBase}/auth/v1/oauth/`
  const strategy = 'microsoft'
  return `${baseURL}${strategy}`
})

onMounted(() => {
  processAuthentication()
})

const requireMessage = ref('')
const validateRequired = (value) => {
  // If the field is empty
  if (!value) {
    requireMessage.value = t('validation.required')
    return requireMessage.value
  }
  // All is good
  return true
}

const validateUserNameRequired = (value) => {
  // If the field is empty
  if (!value) {
    requireMessage.value = t('validation.required')
    return requireMessage.value
  }
  if (!value.includes('.')) {
    requireMessage.value = t('validation.username_tenant_required')
    return requireMessage.value
  }
  // All is good
  return true
}

// Reference for the form to reset it
const formRef = ref(null)
const resetForm = () => {
  formRef.value.resetForm() // This will reset the form
  page.localAuth = false
}

// onMounted(() => {
//   processAuthentication()
// })

// const redirectToMicrosoftOAuth = async () => {
//   try {
//     page.isBusy = true
//     const config = useRuntimeConfig()
//     // Redirect for the login
//     const baseURL = `${config.public.apiBase}/auth/v1/oauth/`
//     const strategy = 'microsoft'
//     const finalURL = `${baseURL}${strategy}`
//     window.location.href = finalURL
//   }
//   catch (error) {
//     // Handle authentication error
//     $notification.error({
//       message: t('auth.auth_failed'),
//     })
//   }
// }

const page = reactive({
  localAuth: false,
  isBusy: true,
  isEmailValidated: false,
})

const onSubmit = async (values) => {
  page.isBusy = true
  return await directLogin(values.username, values.password)
}

const allowedLogin = async (tenant) => {
  const allowedCheckin = await $dataService.checkUserPermissions({ permissionIds: [1002, 1001] }, tenant)
  if (allowedCheckin.length == 0) {
    $notification.error({
      ...error,
      message: t('auth.auth_failed_use_contractor'),
    })
    return false
  }
  return true
}

const directLogin = async (username, password) => {
  let tenant
  let login = username
  page.isBusy = true
  // Checks whether a tenant is specified
  if (!username.includes('.')) {
    $notification.error({
      ...error,
      message: t('auth.tenant_required'),
    })
    return
  }
  // Split the username from the tenant
  const loginSplit = username.split('.')
  tenant = loginSplit[0]
  login = loginSplit.slice(1).join('.')

  try {
    const authResult = await $dataService.authenticate('local', {
      login: username, // We send the tenant with the username, the service strips it out.  This is needed to get the user from SkyV
      password: password,
    })
    if (!await allowedLogin(authResult.tenant)) {
      return
    }
    // console.log(JSON.stringify(authResult))
    // Get the user from the result
    const { user } = authResult
    setUser({
      userId: user.id,
      displayName: user.displayName,
      login: user.login,
    })

    // SkyV redirect takes car of the tenant lookup
    // TODO: Refactor so that Redirect ONLY redirects

    await redirectToSkyVisitor(user, tenant)
  }
  catch (error) {
    // ! Local user not found, shall we try and log into the remote database and copy the user across?
    //    This is supposed to happen in roci-api-auth for usernames containing the tenant name
    setTimeout(() => {
      // Shows indication longer, but also protectects against attacks (a little)
      page.isBusy = false
    }, 1500)
    if (error.message.includes('Please use SSO authentication.')) {
      $notification.error({
        ...error,
        message: t('auth.auth_failed_use_sso'),
      })
    }
    else {
      // TODO: display a nice error here
      // TODO: user the notification plugin
      $notification.error({
        ...error,
        message: t('auth.auth_failed'),
      })
    }
  }

  return { tenant, login }
}

async function redirectToSkyVisitor(user, tenantPrefix) {
  if (!await allowedLogin(tenantPrefix)) {
    return
  }

  let tenants
  let rootDomain = window.location.hostname
  const index = rootDomain.indexOf('.')
  if (index > -1) rootDomain = rootDomain.substring(index)

  if (tenantPrefix) {
    const cookie = `tenant=${tenantPrefix};expires=${new Date(Date.now() + 86400000)};path=/;domain=${rootDomain};`
    document.cookie = cookie
  }
  page.isBusy = true
  // Get the tenant by database name
  tenants = await $dataService.findTenants({
    query: {
      database: tenantPrefix,
      $limit: 5000,
    },
  })

  // Check for the goodness
  if (tenants.total != 1) {
    // ! This database likely exist on multiple servers...not good!
    throw `Database found on these servers, can't decide: ${JSON.stringify(
      tenants.data.map(t => t.server),
    )}`
  }

  const tenantRecord = tenants.data[0]
  try {
    const redirectUrl = await $dataService.findSkyVisitor({
      query: {
        login: user.login,
        server: tenantRecord.server,
        database: tenantRecord.database,
      },
    })
    // Add the auth token to the local storage
    window.location.href = redirectUrl.url
  }
  catch (error) {
    $notification.error({
      ...error,
      message: t('auth.skyvisitor_redirect_error'),
    })
  }
  finally {
    page.isBusy = false
  }
}

async function processAuthentication() {
  try {
    // Authenticate if there is a token in the local storage
    // It will throw an exception if there isn't a token
    if (window.location.hash.includes('access_token')) {
      // Remove feathers-jwt from local storage if present
      localStorage.removeItem('feathers-jwt')
    }
    await $dataService.reAuthenticate()
    if (!await allowedLogin()) {
      return
    }
    // console.log('>>> Reauthenticated')
    // Get the tenants for the user
    // If we have more than one, show selection page
    // If not, redirect to SkyV
    const { user, authentication } = await $dataService.getCurrentUser()
    const w = await $dataService.getCurrentUser()
    setUser({
      userId: user.id,
      displayName: user.displayName,
      login: user.login,
    })
    // TODO: This could potentially be removed
    redirectToSkyVisitor(user, authentication.payload.tenant)
  }
  catch (error) {
    if (
      error.message.includes('No accessToken found in storage')
      || error.message.includes('jwt expired')
      || error.name === 'TokenExpiredError'
    ) {
      // This is normal - FeathersJS throws an exception
      // console.log('>>> JWT Error', error)
      return
    }
    // Show error
    console.error(error.message)
    let errorMessage = t(error.message)
    if (error.message === errorMessage) {
      // If the error message is the same as the translation, then it's not translated
      errorMessage = t('general.errorMessage')
    }
    $notification.error({
      ...error,
      message: errorMessage,
    })
  }
  finally {
    page.isBusy = false
  }
}

function getTenantsAndRedirect(user) {
  // console.log('User', user)
  $dataService
    .findTenantUsers({
      query: {
        userId: user.id,
      },
    })
    .then(async (tenantUserResults) => {
      // console.log('>>> tenantUserResults', tenantUserResults)
      if (tenantUserResults.total == 0) {
        // ERROR: No tenants!
        throw 'auth.no_tenant'
      }
      else if (tenantUserResults.total == 1) {
        // Only one tenant, so redirect to SkyV - NOT tenants selection
        const tenantUserRecord = tenantUserResults.data[0]

        const tenants = await $dataService.findTenants({
          query: {
            id: tenantUserRecord.tenantId,
          },
        })
        const tenantData = tenants.data[0]

        await redirectToSkyVisitor(user, tenantData.database)
      }
      else {
        // Redirect to Tenant selection
        setUserTenants(tenantUserResults.data)
        await navigateTo('/tenants')
      }
    })
}
</script>
